import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { HelpTooltip } from "../cms/HelpTooltip";
import type { OrderReviewRow, ProductMatch } from "./TakeoffOrderDialog";
import { TakeoffProductPicker } from "./TakeoffProductPicker";
import {
  normalizeUnit,
  orderQuantity,
  unitLabel,
  needsSameUnitReset,
} from "./takeoffQuantity";

export function MatchRow({
  row,
  reviewId,
  onChange,
  allowConversions,
}: {
  allowConversions: boolean;
  row: OrderReviewRow;
  reviewId: number;
  onChange: (row: OrderReviewRow) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const update = (patch: Partial<OrderReviewRow>) =>
    onChange({ ...row, confirmed: false, ...patch });
  const sameUnit =
    !!row.product &&
    normalizeUnit(row.product.unit) === normalizeUnit(row.unit);
  const needsReset =
    !!row.product &&
    needsSameUnitReset(
      row.unit,
      row.product.unit,
      row.factor,
      row.coverage_quantity,
    );
  const factor = sameUnit ? "1" : row.factor;
  const coverage = sameUnit ? "1" : (row.coverage_quantity ?? "1");
  const quantity = orderQuantity(row.quantity, factor, coverage);
  const select = (product: ProductMatch) => {
    const same = normalizeUnit(product.unit) === normalizeUnit(row.unit);
    update({
      product,
      factor: same ? "1" : "",
      coverage_quantity: "1",
      conversion_source: same ? "Same ordering unit" : "",
      length_value: null,
      length_uom: "",
    });
    setPickerOpen(false);
  };
  const changeCoverage = (patch: {
    factor?: string;
    coverage_quantity?: string;
  }) => {
    update({
      ...patch,
      conversion_source: `User confirmed: ${patch.factor ?? factor} ${row.product?.unit} covers ${patch.coverage_quantity ?? coverage} ${row.unit}`,
    });
  };
  return (
    <Card
      variant="outlined"
      sx={{ p: 0, gap: 0, opacity: row.excluded ? 0.7 : 1, overflow: "hidden" }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={1}
        sx={{
          p: 2,
          bgcolor: "background.level1",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography level="title-lg">{row.material}</Typography>
          <Typography level="body-xs">{row.source}</Typography>
        </Box>
        <Chip
          color={
            row.excluded ? "neutral" : row.confirmed ? "success" : "warning"
          }
          size="sm"
        >
          {row.excluded
            ? "Excluded"
            : row.confirmed && !needsReset
              ? "Confirmed"
              : row.product
                ? "Review selection"
                : "Choose product"}
        </Chip>
      </Stack>
      <Stack spacing={2} sx={{ p: { xs: 2, md: 2.5 } }}>
        <Checkbox
          label="Exclude from this order"
          checked={row.excluded}
          onChange={(e) => update({ excluded: e.target.checked })}
        />
        {row.excluded ? (
          <Input
            aria-label={`Reason for excluding ${row.material}`}
            placeholder="Why is this item excluded?"
            value={row.exclusion_reason}
            onChange={(e) => update({ exclusion_reason: e.target.value })}
          />
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(190px, 1fr) minmax(0, 2fr)",
                },
                gap: 2,
              }}
            >
              <Stack
                spacing={1}
                sx={{ bgcolor: "background.level1", p: 2, borderRadius: "md" }}
              >
                <Typography level="title-sm">1. Takeoff quantity</Typography>
                <FormControl>
                  <FormLabel>
                    Required {unitLabel(row.unit)}{" "}
                    <HelpTooltip
                      title="Required quantity"
                      content="This is the saved takeoff quantity, including its waste allowance. Adjust it if needed. We do not add waste again when creating the order."
                    />
                  </FormLabel>
                  <Input
                    aria-label={`Required quantity for ${row.material}`}
                    type="number"
                    value={row.quantity}
                    slotProps={{ input: { min: 0.001, step: 0.001 } }}
                    onChange={(e) => update({ quantity: e.target.value })}
                  />
                </FormControl>
                <Typography level="body-xs">
                  Includes the takeoff’s waste allowance.
                </Typography>
              </Stack>
              <Stack
                spacing={1}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "md",
                  minWidth: 0,
                }}
              >
                <Typography level="title-sm">
                  2. Distributor product{" "}
                  <HelpTooltip
                    title="Choose an ordering unit"
                    content="Select the exact product and color. Choosing the same unit as the takeoff avoids a conversion. Prices are verified in Create Order."
                  />
                </Typography>
                {row.product ? (
                  <>
                    <Typography
                      level="title-md"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {row.product.name}
                    </Typography>
                    <Typography level="body-xs">
                      SKU {row.product.sku || "Not supplied"} · Sold in{" "}
                      {unitLabel(row.product.unit)} ({row.product.unit})
                    </Typography>
                    <Typography level="body-xs">
                      {row.product.price == null
                        ? "Pricing verified in Create Order"
                        : `$${row.product.price} per ordering unit`}
                    </Typography>
                  </>
                ) : (
                  <Typography level="body-sm">
                    Search your distributor’s catalog to choose the brand,
                    product and color.
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  startDecorator={<MagnifyingGlass />}
                  onClick={() => setPickerOpen(true)}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {row.product ? "Change product" : "Find product"}
                </Button>
              </Stack>
            </Box>
            {row.product && !sameUnit && !allowConversions && (
              <Alert color="warning">
                Choose a product sold in {unitLabel(row.unit)} to continue.
                Converting between units will be available after the server
                update.
              </Alert>
            )}
            {row.product && !sameUnit && allowConversions && (
              <Box
                sx={{ p: 2, bgcolor: "background.level1", borderRadius: "md" }}
              >
                <Typography level="title-sm" sx={{ mb: 0.5 }}>
                  How much does this product cover?{" "}
                  <HelpTooltip
                    title="Convert to the distributor’s ordering unit"
                    content="Use the product’s packaging or manufacturer specifications. Enter how many ordering units cover a quantity from your takeoff. We divide the required quantity by this coverage and round up to a whole ordering unit."
                    example="1 square covers 3 bundles. For 137 bundles, order 46 squares."
                  />
                </Typography>
                <Typography level="body-sm" sx={{ mb: 1.5 }}>
                  Your takeoff uses {unitLabel(row.unit)}; this product is sold
                  in {unitLabel(row.product.unit)}.
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ sm: "flex-end" }}
                >
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>
                      Ordering quantity ({unitLabel(row.product.unit)})
                    </FormLabel>
                    <Input
                      aria-label={`Ordering quantity for ${row.material}`}
                      type="number"
                      placeholder="e.g. 1"
                      value={factor}
                      slotProps={{ input: { min: 0.000001, step: "any" } }}
                      onChange={(e) =>
                        changeCoverage({ factor: e.target.value })
                      }
                    />
                  </FormControl>
                  <Typography sx={{ pb: { sm: 1 } }}>covers</Typography>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>
                      Takeoff quantity ({unitLabel(row.unit)})
                    </FormLabel>
                    <Input
                      aria-label={`Coverage quantity for ${row.material}`}
                      type="number"
                      placeholder="e.g. 3"
                      value={coverage}
                      slotProps={{ input: { min: 0.000001, step: "any" } }}
                      onChange={(e) =>
                        changeCoverage({ coverage_quantity: e.target.value })
                      }
                    />
                  </FormControl>
                </Stack>
                <Typography level="body-xs" sx={{ mt: 1 }}>
                  Check the package or manufacturer’s coverage before
                  confirming. Your confirmed relationship is saved with this
                  review.
                </Typography>
              </Box>
            )}
            {row.product?.is_dimensional && (
              <FormControl>
                <FormLabel>
                  Product length{" "}
                  <HelpTooltip
                    title="Product length"
                    content="Select the manufacturer’s available length for this product. It will be included on the materials order."
                  />
                </FormLabel>
                <Select
                  aria-label={`Length for ${row.material}`}
                  value={
                    row.length_value
                      ? `${Number(row.length_value)}:${row.length_uom}`
                      : null
                  }
                  onChange={(_, value) => {
                    const selected = row.product?.dimension_options?.find(
                      (v) => `${v.value}:${v.uom}` === value,
                    );
                    update({
                      length_value: selected ? String(selected.value) : null,
                      length_uom: selected?.uom || "",
                    });
                  }}
                  placeholder="Choose a manufacturer length"
                >
                  {row.product.dimension_options?.map((v) => (
                    <Option
                      key={`${v.value}:${v.uom}`}
                      value={`${v.value}:${v.uom}`}
                    >
                      {v.size || `${v.value} ${v.uom}`}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl>
              <FormLabel>Item notes (optional)</FormLabel>
              <Input
                aria-label={`Notes for ${row.material}`}
                value={row.notes}
                onChange={(e) => update({ notes: e.target.value })}
              />
            </FormControl>
          </>
        )}
      </Stack>
      {!row.excluded && (
        <Stack
          spacing={1.5}
          sx={{
            p: 2,
            bgcolor: row.confirmed ? "success.softBg" : "primary.softBg",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <Box>
              <Typography level="title-sm">
                3. Quantity to order{" "}
                <HelpTooltip
                  title="Final ordering quantity"
                  content="This quantity will be carried into your draft materials order. Partial ordering units are rounded up. No additional waste is added. Creating a draft does not send an order to the distributor."
                />
              </Typography>
              {row.product && quantity !== null && (
                <Typography level="body-xs">
                  {sameUnit
                    ? "Same unit as your takeoff; no conversion needed."
                    : `${row.quantity} ${unitLabel(row.unit)} × ${factor} ÷ ${coverage}, rounded up.`}
                </Typography>
              )}
            </Box>
            <Typography level="title-lg">
              {row.product && quantity !== null
                ? `${quantity.toLocaleString()} ${unitLabel(row.product.unit)}`
                : "—"}
            </Typography>
          </Stack>
          {needsReset && (
            <Typography color="warning" level="body-sm">
              This saved item used a conversion even though the units match.
              Confirm the corrected quantity shown above.
            </Typography>
          )}
          <Checkbox
            label="I confirmed the product, color and ordering quantity"
            checked={row.confirmed && !needsReset}
            disabled={
              (!sameUnit && !allowConversions) ||
              !row.product ||
              quantity === null ||
              (row.product.is_dimensional && !row.length_value)
            }
            onChange={(e) =>
              update({
                confirmed: e.target.checked,
                ...(sameUnit
                  ? {
                      factor: "1",
                      coverage_quantity: "1",
                      conversion_source: "Same ordering unit",
                    }
                  : {}),
              })
            }
          />
        </Stack>
      )}
      {pickerOpen && (
        <TakeoffProductPicker
          reviewId={reviewId}
          material={row.material}
          takeoffUnit={row.unit}
          initialSearch={row.search_hint || row.material}
          onSelect={select}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </Card>
  );
}
